import { createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'
import {
  NotificationPayload,
  showNotification,
} from 'src/features/notifications'
import { fx } from 'src/libs'
import { BASE_URL } from 'src/pages/worldCup2026/const'
import {
  Player,
  PlayerListResponse,
  TeamListResponse,
  PlayerParameter,
} from 'src/pages/worldCup2026/players/types'

export const PlayerListGate = createGate()

export const loadPlayerListByTeam = createEvent<{ id: number }>()
export const updatePlayerList = createEvent<{
  teamID: number
  playerList: PlayerParameter[]
}>()

export const getTeamsFx = fx<void | any, TeamListResponse>({
  method: 'GET',
  url: `${BASE_URL}teams`,
})

export const getPlayersFx = fx<void | any, PlayerListResponse>({
  method: 'GET',
  url: `${BASE_URL}players`,
})

export const updatePlayerFx = fx<void | any, PlayerListResponse>({
  method: 'POST',
  url: `${BASE_URL}players/parameters`,
})

export const $teamOptions = createStore<{ label: string; value: number }[]>(
  [],
).on(getTeamsFx.doneData, (_, res) => {
  return res.body.items
    .map(({ name, id }) => ({ label: name, value: id }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

export const $players = createStore<Player[]>([]).on(
  getPlayersFx.doneData,
  (_, res) => res.body.items,
)

sample({
  clock: PlayerListGate.open,
  fn: () => ({}),
  target: getTeamsFx,
})

sample({
  clock: updatePlayerList,
  source: $players,
  fn: (playersStore, { playerList, teamID }) => {
    const players = playersStore.map((item) => {
      const parameters = playerList.find((it) => it.id === item.id)
      return parameters
        ? {
            ...item,
            price: String(parameters.price),
            level: parameters.level,
            isTop: parameters.isTop,
            shirtNumber: parameters.shirtNumber,
          }
        : item
    })
    return { body: { teamID, players } }
  },
  target: updatePlayerFx,
})

sample({
  clock: loadPlayerListByTeam,
  fn: ({ id }) => {
    const params = new URLSearchParams({
      teamID: String(id),
    })
    return { url: `${BASE_URL}players?${params}` }
  },
  target: getPlayersFx,
})

sample({
  clock: updatePlayerFx.done,
  source: $players,
  fn: (_, { params }) => {
    const { body } = params
    const { players } = body
    return players
  },
  target: $players,
})

sample({
  clock: [getPlayersFx.failData, updatePlayerFx.failData],
  fn: (error): NotificationPayload => ({
    type: 'error',
    message: 'Request failed',
    description: error?.body?.message ?? 'Request failed',
  }),
  target: showNotification,
})

sample({
  clock: updatePlayerFx.doneData,
  fn: (): NotificationPayload => ({
    type: 'success',
    message: 'Success',
    description: 'Player data updated successfully',
  }),
  target: showNotification,
})
